import React, { ChangeEvent, FormEvent, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Button, Dialog, Divider, Grid, IconButton, Typography, WithStyles } from "@material-ui/core"
import { createStyles, makeStyles, Theme, withStyles } from "@material-ui/core/styles"
import MuiDialogTitle from "@material-ui/core/DialogTitle"
import MuiDialogContent from "@material-ui/core/DialogContent"
import { Close } from "@material-ui/icons"
import demoformHackle from "../svgs/demoform-hackle.svg"
import netflix from "../svgs/netflix.svg"
import grap from "../svgs/grap.svg"
import { GAEvent } from "../../modules/GoogleAnalytics"
import usePlatform from "../../hooks/usePlatform"
import { useTrack } from "@hackler/react-sdk"
import { requestDemo } from "../../apis/requestDemoApi"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: "40px 20px",
      background: "#FFFFFF",
    },
    defaultButton: {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.common.white,
      textTransform: "none",
      fontSize: "16px",
      [theme.breakpoints.up("md")]: {
        fontSize: "16px",
        padding: "10px 20px",
      },
      "&:hover": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
      },
    },
    desc: {
      color: "rgba(0,0,0,0.6)",
      fontSize: "12px",
      [theme.breakpoints.up("md")]: {
        fontSize: "16px",
        marginBottom: theme.spacing(2),
      },
    },
    input: {
      padding: "10px 10px",
      fontSize: "12px",
      width: "100%",
      border: "1px solid #EDEDED",
      "&::placeholder": {
        color: "rgba(0, 0, 0, 0.3)",
      },
      [theme.breakpoints.up("md")]: {
        padding: "12px 20px",
        fontSize: "16px",
      },
    },
    textArea: {
      width: "100%",
      height: "84px",
      boxSizing: "border-box",
      fontFamily: "inherit",
      fontSize: "12px",
      padding: "10px 10px",
      border: "1px solid #EDEDED",
      resize: "none",
      "&::placeholder": {
        color: "rgba(0, 0, 0, 0.3)",
      },
      [theme.breakpoints.up("md")]: {
        padding: "12px 20px",
        height: "144px",
        fontSize: "16px",
      },
    },
    btn: {
      height: "40px",
      fontSize: "16px",
      textTransform: "none",
      [theme.breakpoints.up("md")]: {
        height: "60px",
        fontSize: "20px",
      },
    },
    resultTitle: {
      color: "rgba(0,0,0,0.87)",
      fontSize: "20px",
      textAlign: "start",
      [theme.breakpoints.up("md")]: {
        fontSize: "24px",
      },
    },
    text: {
      fontSize: "12px",
      color: "rgba(0,0,0,0.6)",
    },
    img: {
      width: "100px",
      [theme.breakpoints.up("md")]: {
        width: "160px",
      },
    },
    btnMore: {
      border: `1px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      textTransform: "none",
    },
  })
)

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing("20px", "20px", "8px"),
      [theme.breakpoints.up("md")]: {
        padding: theme.spacing(5, 5, 2),
      },
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: "14px",
      color: theme.palette.grey[500],
      [theme.breakpoints.up("md")]: {
        right: theme.spacing(3),
        top: theme.spacing(4),
      },
    },
  })

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string
  children: React.ReactNode
  onClose: () => void
}

export const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <Close />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  )
})

export const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(0, "20px", "20px", "20px"),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(0, 5, 5, 5),
    },
  },
}))(MuiDialogContent)

const defaultFormData = {
  type: "REQUEST_DEMO",
  email: "",
  name: "",
  contact: "",
  comment: "",
}
interface Props {
  ButtonElement?(handleButtonClick: () => void): React.ReactElement
}
export default function DemoRequestFormDialog({ ButtonElement }: Props) {
  const track = useTrack()
  const classes = useStyles()
  const [scene, setScene] = useState<"REQUEST" | "RESULT">("REQUEST")
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState(defaultFormData)
  const intl = useIntl()
  const handleButtonClick = () => {
    handleClickOpen()
  }

  const handleClickOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
    setTimeout(() => {
      setScene("REQUEST")
    }, 100)
  }
  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value
    setFormData({
      ...formData,
      [event.target.name]: value,
    })
  }

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    track({ key: "homepage_home_request_demo_request_click" })
    const requestParam = { ...formData, comment: `${formData.comment}` }
    if (await requestDemo(requestParam)) {
      setScene("RESULT")
      setFormData(defaultFormData)
    }
  }
  const { isMobile } = usePlatform()

  const RequestDialog = () => {
    return (
      <>
        <DialogTitle id="customized-dialog-title" onClose={handleClose}>
          <strong>
            <FormattedMessage id={"REQUEST_DEMO_TITLE"} />
          </strong>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={onSubmit}>
            <Grid container spacing={isMobile ? 1 : 2}>
              <Grid item xs={12}>
                <input
                  required
                  type={"email"}
                  name={"email"}
                  className={classes.input}
                  placeholder={intl.formatMessage({ id: "REQUIRED_EMAIL" })}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  name={"name"}
                  className={classes.input}
                  placeholder={intl.formatMessage({ id: "NAME" })}
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  name={"contact"}
                  className={classes.input}
                  placeholder={intl.formatMessage({ id: "PHONE_NUMBER" })}
                  value={formData.contact}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <textarea
                  name={"comment"}
                  className={classes.textArea}
                  onChange={handleInputChange}
                  value={formData.comment}
                  placeholder={intl.formatMessage({ id: "REQUEST_DEMO_COMMENT" })}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type={"submit"}
                  color={"primary"}
                  variant={"contained"}
                  fullWidth
                  className={classes.btn}
                  onClick={() => {
                    GAEvent({
                      category: "demo dialog",
                      action: "click",
                      label: "request demo",
                    })

                    try {
                      //@ts-ignore
                      analytics.track("homepage_demo_request_complete")
                    } catch (error) {}
                  }}>
                  <strong>
                    <FormattedMessage id={"BTN_REQUEST"} />
                  </strong>
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </>
    )
  }
  const ResultDialog = () => {
    return (
      <>
        <DialogTitle id="customized-dialog-title" onClose={handleClose}>
          <strong>
            <FormattedMessage id={"REQUESTED_DEMO_TITLE"} />
          </strong>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className={classes.desc}>
                <FormattedMessage id={"REQUESTED_DEMO_DESC1"} /> <br /> <FormattedMessage id={"REQUESTED_DEMO_DESC2"} />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Typography variant={"h3"} className={classes.resultTitle}>
                <FormattedMessage id={"REQUESTED_DEMO_READ_MORE1"} /> {isMobile && <br />}
                <FormattedMessage id={"REQUESTED_DEMO_READ_MORE2"} />
                <br /> <FormattedMessage id={"REQUESTED_DEMO_READ_MORE3"} />
              </Typography>
            </Grid>
            {isMobile ? (
              <>
                <Grid item xs={12}>
                  <Grid container spacing={2} justify={"flex-start"}>
                    <Grid item xs={"auto"}>
                      <img src={demoformHackle} className={classes.img} alt={"hackle-demo"} />
                    </Grid>
                    <Grid item xs={7}>
                      <Grid container spacing={1}>
                        <Grid item>
                          <Typography className={classes.text}>
                            <FormattedMessage id={"BLOG_HACKLE_DESC"} />
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Button
                            variant={"outlined"}
                            className={classes.btnMore}
                            component={"a"}
                            href={
                              "https://medium.com/hackle/a-b-%ED%85%8C%EC%8A%A4%ED%8A%B8%EC%9D%98-%ED%95%84%EC%9A%94%EC%84%B1-876a4f1c02fa"
                            }
                            target={"_blank"}>
                            <FormattedMessage id={"BTN_READ_MORE"} />
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2} justify={"flex-start"}>
                    <Grid item xs={"auto"}>
                      <img src={netflix} className={classes.img} alt={"hackle-demo"} />
                    </Grid>
                    <Grid item xs={7}>
                      <Grid container spacing={1}>
                        <Grid item>
                          <Typography className={classes.text}>
                            <FormattedMessage id={"BLOG_NETFLIX_DESC"} />
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Button
                            variant={"outlined"}
                            className={classes.btnMore}
                            component={"a"}
                            href={
                              "https://medium.com/hackle/a-b-%ED%85%8C%EC%8A%A4%ED%8A%B8%EB%A5%BC-%ED%86%B5%ED%95%B4-%EB%84%B7%ED%94%8C%EB%A6%AD%EC%8A%A4-%EC%98%81%EC%83%81%EC%97%90-%EA%B0%80%EC%9E%A5-%EC%A0%81%ED%95%A9%ED%95%9C-%EC%82%BD%ED%99%94-%EC%B0%BE%EA%B8%B0-a086fa3e10c6"
                            }
                            target={"_blank"}>
                            <FormattedMessage id={"BTN_READ_MORE"} />
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2} justify={"flex-start"}>
                    <Grid item xs={"auto"}>
                      <img src={grap} className={classes.img} alt={"hackle-demo"} />
                    </Grid>
                    <Grid item xs={7}>
                      <Grid container spacing={1}>
                        <Grid item>
                          <Typography className={classes.text}>
                            <FormattedMessage id={"BLOG_GRAB_DESC"} />
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Button
                            variant={"outlined"}
                            className={classes.btnMore}
                            component={"a"}
                            href={
                              "https://medium.com/hackle/a-b-%ED%85%8C%EC%8A%A4%ED%8A%B8%EB%A5%BC-%ED%86%B5%ED%95%B4-%EA%B7%B8%EB%9E%A9-%EB%93%9C%EB%9D%BC%EC%9D%B4%EB%B2%84%EC%9D%98-%EC%B0%B8%EC%97%AC%EB%8F%84-%EB%86%92%EC%9D%B4%EA%B8%B0-grabbing-growth-a-growth-hacking-story-de0621513dc6"
                            }
                            target={"_blank"}>
                            <FormattedMessage id={"BTN_READ_MORE"} />
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={4}>
                  <img src={demoformHackle} className={classes.img} alt={"hackle-demo"} />
                  <br />
                  <br />
                  <Typography className={classes.text}>
                    <FormattedMessage id={"BLOG_HACKLE_DESC"} />
                  </Typography>
                  <br />
                  <Button
                    variant={"outlined"}
                    className={classes.btnMore}
                    component={"a"}
                    href={
                      "https://medium.com/hackle/a-b-%ED%85%8C%EC%8A%A4%ED%8A%B8%EC%9D%98-%ED%95%84%EC%9A%94%EC%84%B1-876a4f1c02fa"
                    }
                    target={"_blank"}>
                    <FormattedMessage id={"BTN_READ_MORE"} />
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <img src={netflix} className={classes.img} alt={"hackle-demo"} />
                  <br />
                  <br />
                  <Typography className={classes.text}>
                    <FormattedMessage id={"BLOG_NETFLIX_DESC"} />
                  </Typography>
                  <br />
                  <Button
                    variant={"outlined"}
                    className={classes.btnMore}
                    component={"a"}
                    href={
                      "https://medium.com/hackle/a-b-%ED%85%8C%EC%8A%A4%ED%8A%B8%EB%A5%BC-%ED%86%B5%ED%95%B4-%EB%84%B7%ED%94%8C%EB%A6%AD%EC%8A%A4-%EC%98%81%EC%83%81%EC%97%90-%EA%B0%80%EC%9E%A5-%EC%A0%81%ED%95%A9%ED%95%9C-%EC%82%BD%ED%99%94-%EC%B0%BE%EA%B8%B0-a086fa3e10c6"
                    }
                    target={"_blank"}>
                    <FormattedMessage id={"BTN_READ_MORE"} />
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <img src={grap} className={classes.img} alt={"hackle-demo"} />
                  <br />
                  <br />
                  <Typography className={classes.text}>
                    <FormattedMessage id={"BLOG_GRAB_DESC"} />
                  </Typography>
                  <br />
                  <Button
                    variant={"outlined"}
                    className={classes.btnMore}
                    component={"a"}
                    href={
                      "https://medium.com/hackle/a-b-%ED%85%8C%EC%8A%A4%ED%8A%B8%EB%A5%BC-%ED%86%B5%ED%95%B4-%EA%B7%B8%EB%9E%A9-%EB%93%9C%EB%9D%BC%EC%9D%B4%EB%B2%84%EC%9D%98-%EC%B0%B8%EC%97%AC%EB%8F%84-%EB%86%92%EC%9D%B4%EA%B8%B0-grabbing-growth-a-growth-hacking-story-de0621513dc6"
                    }
                    target={"_blank"}>
                    <FormattedMessage id={"BTN_READ_MORE"} />
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
      </>
    )
  }
  return (
    <>
      <div style={{ textAlign: "center" }}>
        {ButtonElement ? (
          ButtonElement(handleButtonClick)
        ) : (
          <Button color={"default"} fullWidth={isMobile} className={classes.defaultButton} onClick={handleButtonClick}>
            <strong>
              <FormattedMessage id={"BTN_REQUEST_DEMO"} />
            </strong>
          </Button>
        )}
      </div>
      <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
        {scene === "REQUEST" ? RequestDialog() : ResultDialog()}
      </Dialog>
    </>
  )
}
